import * as ReactDOM from "react-dom/client";
import { StrictMode } from "react";
import { Provider } from "react-redux";
import { JssProvider } from "react-jss";
import { Capacitor } from "@capacitor/core";

import {
  setup,
  AuthManagerSetupOptions,
  KeycloakManagerOptions,
  OAuth2CapacitorManagerOptions,
  DeviceAuthManagerOptions,
} from "@onmo/auth";

import { store } from "./store";
import App from "./app";
import "./styles.css";
import { configSafeArea } from "./configs/configSafeArea";
import { ScaledThemeProvider } from "./components/common/ScaledThemeProvider/ScaledThemeProvider";
import { AppReloadPrompt } from "./components/common/AppReloadPrompt/AppReloadPrompt";
import { SplashScreen } from "./components/common/SplashScreen/SplashScreen";
import { PageRoutes } from "./constants/routes";
import { isTV, isQRCodeLoginEnabled } from "./utils/platform";
import { prefetchDeviceCapabilities } from "./utils/deviceDetection";
import jss from "./utils/jssPreset";
import * as Sentry from "@sentry/react";
import { configureSentry } from "./configs/sentry";
import FallbackError from "./components/common/FallbackError/FallbackError";

configSafeArea();
configureSentry();

// Warm up the device capabilities cache (native bridges + codec probes) off the
// critical path — idle, so late-injected native bridges are already there.
const warmUpDeviceCapabilities = () => {
  prefetchDeviceCapabilities().catch(() => undefined);
};
if (typeof window.requestIdleCallback === "function") {
  window.requestIdleCallback(warmUpDeviceCapabilities, { timeout: 5000 });
} else {
  setTimeout(warmUpDeviceCapabilities, 2000);
}

if (import.meta.env.VITE_ENABLE_DEBUG_CONSOLE && "ontouchstart" in window) {
  import("eruda").then(({ default: eruda }) => eruda.init());
}

// Determine auth provider based on platform
function getAuthOptions(): AuthManagerSetupOptions {
  // TV or QR code login enabled → Device Auth

  // Native mobile (Capacitor) → OAuth2
  if (Capacitor.isNativePlatform() && !isTV) {
    const options: OAuth2CapacitorManagerOptions = {
      provider: "oauth2-capacitor",
    };
    return options;
  }

  if (isQRCodeLoginEnabled || isTV) {
    const options: DeviceAuthManagerOptions = {
      provider: "device-auth",
      qrCodeRedirectUrl: PageRoutes.LoginQR,
    };
    return options;
  }
  // Web → Keycloak
  const options: KeycloakManagerOptions = {
    provider: "keycloak",
    onLoad: "check-sso",
  };
  return options;
}

setup(store, getAuthOptions());

if (isTV) {
  // update meta viewport to 1/window.devicePixelRatio for Android TV
  document
    .querySelector("meta[name='viewport']")
    ?.setAttribute(
      "content",
      `width=device-width, initial-scale=${
        1 / window.devicePixelRatio
      }, maximum-scale=${1 / window.devicePixelRatio}, user-scalable=no`,
    );
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<FallbackError />}>
      <Provider store={store}>
        <JssProvider jss={jss}>
          <ScaledThemeProvider>
            <SplashScreen>
              <App />
              <AppReloadPrompt />
            </SplashScreen>
          </ScaledThemeProvider>
        </JssProvider>
      </Provider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
